import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Plus,
  X,
  Trash2,
  CheckCircle
} from 'lucide-react';
import api from '../../utils/api';
import './AdmissionsPage.css';

const AdmissionsPage = () => {
  // State for stats
  const [stats, setStats] = useState({
    applied: 0,
    underReview: 0,
    interviewed: 0,
    accepted: 0,
    rejected: 0,
    enrolled: 0
  });

  // State for admissions
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    searchStudent: '',
    class: '',
    status: ''
  });

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEnrollConfirm, setShowEnrollConfirm] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    applyingForClass: '',
    previousSchool: '',
    fatherName: '',
    motherName: '',
    contactPhone: '',
    contactEmail: '',
    address: ''
  });

  // Status change form
  const [statusForm, setStatusForm] = useState({
    recordId: '',
    newStatus: ''
  });

  // Constants
  const CLASSES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  const STATUSES = ['applied', 'under-review', 'interview', 'accepted', 'rejected', 'enrolled'];
  const STATUS_COLORS = {
    'applied': '#3b82f6',
    'under-review': '#f59e0b',
    'interview': '#b8860b',
    'accepted': '#10b981',
    'rejected': '#ef4444',
    'enrolled': '#67e8f9'
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/admissions/stats');
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Fetch admissions
  const fetchAdmissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.searchStudent) params.append('student', filters.searchStudent);
      if (filters.class) params.append('class', filters.class);
      if (filters.status) params.append('status', filters.status);

      const response = await api.get(`/admissions?${params.toString()}`);
      const data = response.data.items || response.data;
      setAdmissions(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching admissions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchAdmissions();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Form handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      applyingForClass: '',
      previousSchool: '',
      fatherName: '',
      motherName: '',
      contactPhone: '',
      contactEmail: '',
      address: ''
    });
  };

  const handleAddApplication = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admissions', formData);
      setMessage('Application added successfully');
      setShowAddModal(false);
      resetForm();
      fetchAdmissions();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error adding application:', err);
      setError('Failed to add application');
    }
  };

  const handleStatusChange = async (admissionId, newStatus) => {
    try {
      await api.put(`/admissions/${admissionId}/status`, { status: newStatus });
      setMessage('Status updated successfully');
      fetchAdmissions();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const handleEnrollStudent = async () => {
    try {
      await api.post(`/admissions/${selectedAdmission.id}/enroll`, {});
      setMessage('Student enrolled successfully');
      setShowEnrollConfirm(false);
      setSelectedAdmission(null);
      fetchAdmissions();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error enrolling student:', err);
      setError('Failed to enroll student');
    }
  };

  const handleDeleteAdmission = async () => {
    try {
      await api.delete(`/admissions/${selectedAdmission.id}`);
      setMessage('Application deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedAdmission(null);
      fetchAdmissions();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting admission:', err);
      setError('Failed to delete application');
    }
  };

  const getStatusBadgeStyle = (status) => {
    return {
      backgroundColor: STATUS_COLORS[status] || '#6b7280',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'capitalize'
    };
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowDeleteConfirm(false);
    setShowEnrollConfirm(false);
    if (!showAddModal) {
      setSelectedAdmission(null);
      resetForm();
    }
  };

  return (
    <div className="admissions-page">
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {/* Stats Section - Pipeline View */}
      <div className="stats-grid pipeline-stats">
        <div className="stat-card stat-applied">
          <div className="stat-icon">
            <Users size={24} color="#3b82f6" />
          </div>
          <div className="stat-content">
            <h3>Applied</h3>
            <p className="stat-amount">{stats.applied || 0}</p>
          </div>
        </div>

        <div className="stat-card stat-under-review">
          <div className="stat-icon">
            <Users size={24} color="#f59e0b" />
          </div>
          <div className="stat-content">
            <h3>Under Review</h3>
            <p className="stat-amount">{stats.underReview || 0}</p>
          </div>
        </div>

        <div className="stat-card stat-interview">
          <div className="stat-icon">
            <Users size={24} color="#b8860b" />
          </div>
          <div className="stat-content">
            <h3>Interview</h3>
            <p className="stat-amount">{stats.interviewed || 0}</p>
          </div>
        </div>

        <div className="stat-card stat-accepted">
          <div className="stat-icon">
            <Users size={24} color="#10b981" />
          </div>
          <div className="stat-content">
            <h3>Accepted</h3>
            <p className="stat-amount">{stats.accepted || 0}</p>
          </div>
        </div>

        <div className="stat-card stat-rejected">
          <div className="stat-icon">
            <Users size={24} color="#ef4444" />
          </div>
          <div className="stat-content">
            <h3>Rejected</h3>
            <p className="stat-amount">{stats.rejected || 0}</p>
          </div>
        </div>

        <div className="stat-card stat-enrolled">
          <div className="stat-icon">
            <Users size={24} color="#67e8f9" />
          </div>
          <div className="stat-content">
            <h3>Enrolled</h3>
            <p className="stat-amount">{stats.enrolled || 0}</p>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body">
          <div className="filter-bar">
            <div className="filter-group">
              <div className="search-input-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search student name..."
                  name="searchStudent"
                  value={filters.searchStudent}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="filter-group">
              <select
                className="form-control"
                name="class"
                value={filters.class}
                onChange={handleFilterChange}
              >
                <option value="">All Classes</option>
                {CLASSES.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <select
                className="form-control"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                {STATUSES.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
            >
              <Plus size={18} /> New Application
            </button>
          </div>
        </div>
      </div>

      {/* Admissions Table */}
      <div className="card">
        <div className="card-header">
          <h2>Applications</h2>
          <span className="record-count">{admissions.length} records</span>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="empty-state">
              <p>Loading applications...</p>
            </div>
          ) : admissions.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <h3>No applications found</h3>
              <p>No applications match your current filters</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="admissions-table">
                <thead>
                  <tr>
                    <th>App No</th>
                    <th>Student Name</th>
                    <th>Class</th>
                    <th>Parent Name</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Applied Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admissions.map(admission => (
                    <tr key={admission.id}>
                      <td className="app-no">{admission.applicationNo || admission.id}</td>
                      <td className="student-name">{admission.firstName} {admission.lastName}</td>
                      <td>{admission.applyingForClass}</td>
                      <td>{admission.fatherName || admission.motherName || '-'}</td>
                      <td>{admission.contactPhone}</td>
                      <td>
                        <span style={getStatusBadgeStyle(admission.status)}>
                          {admission.status.replace('-', ' ')}
                        </span>
                      </td>
                      <td>{new Date(admission.appliedDate || admission.createdAt).toLocaleDateString('en-IN')}</td>
                      <td className="actions">
                        <select
                          className="form-control-sm status-select"
                          value={admission.status}
                          onChange={(e) => handleStatusChange(admission.id, e.target.value)}
                          title="Change Status"
                        >
                          {STATUSES.map(status => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                            </option>
                          ))}
                        </select>

                        {admission.status === 'accepted' && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => {
                              setSelectedAdmission(admission);
                              setShowEnrollConfirm(true);
                            }}
                            title="Enroll Student"
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}

                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            setSelectedAdmission(admission);
                            setShowDeleteConfirm(true);
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Application Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Application</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddApplication}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      className="form-control"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      className="form-control"
                      name="gender"
                      value={formData.gender}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Applying for Class</label>
                    <select
                      className="form-control"
                      name="applyingForClass"
                      value={formData.applyingForClass}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Class</option>
                      {CLASSES.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Previous School</label>
                  <input
                    type="text"
                    className="form-control"
                    name="previousSchool"
                    value={formData.previousSchool}
                    onChange={handleFormChange}
                    placeholder="School name and location"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Father's Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="fatherName"
                      value={formData.fatherName}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Mother's Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="motherName"
                      value={formData.motherName}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Contact Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    rows="3"
                    placeholder="Full address"
                    required
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModals}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enroll Confirmation Modal */}
      {showEnrollConfirm && selectedAdmission && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Enroll Student</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to enroll <strong>{selectedAdmission.firstName} {selectedAdmission.lastName}</strong> in Class {selectedAdmission.applyingForClass}?</p>
              <p className="text-muted">This will create a student record and complete the admission process.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModals}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleEnrollStudent}
              >
                <CheckCircle size={16} /> Enroll Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedAdmission && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this application?</p>
              <p className="text-muted"><strong>{selectedAdmission.firstName} {selectedAdmission.lastName}</strong></p>
              <p className="text-muted">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModals}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteAdmission}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmissionsPage;
